import {useEffect, useState} from 'react'
import {Card} from 'react-bootstrap'
import {useMutation} from '@apollo/client'
import {defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {
  SEND_AGENT_ONBOARDING_COMMISSION_PROPOSAL,
  UPDATE_AGENT_ONBOARDING_COMMISSION_LEVEL,
} from '../../../gql/mutations/agentOnboardingMutations'

const M = defineMessages({
  title: {id: 'onboarding.commission.title', defaultMessage: 'Commission proposal'},
  label: {id: 'onboarding.commission.label', defaultMessage: 'Proposed commission level'},
  savedLevel: {
    id: 'onboarding.commission.savedLevel',
    defaultMessage: 'Currently saved level: {level}',
  },
  none: {id: 'onboarding.commission.none', defaultMessage: 'none'},
  save: {id: 'onboarding.commission.save', defaultMessage: 'Save level'},
  send: {id: 'onboarding.commission.send', defaultMessage: 'Send proposal to agent'},
  saved: {id: 'onboarding.commission.saved', defaultMessage: 'Commission level saved'},
  saveFailed: {
    id: 'onboarding.commission.saveFailed',
    defaultMessage: 'Could not save the commission level',
  },
  sent: {id: 'onboarding.commission.sent', defaultMessage: 'Proposal sent to the agent'},
  sendFailed: {id: 'onboarding.commission.sendFailed', defaultMessage: 'Could not send the proposal'},
  sentAt: {id: 'onboarding.commission.sentAt', defaultMessage: 'Last sent: {date}'},
})

/** 85% to 150% in steps of 5, matching what the backend accepts. */
const LEVELS = Array.from({length: 14}, (_, i) => 85 + i * 5)

interface Props {
  applicationId: string
  savedLevel: number | null
  sentAt: string | null
  canEdit: boolean
  onSaved: () => void
}

export function CommissionProposal({
  applicationId,
  savedLevel,
  sentAt,
  canEdit,
  onSaved,
}: Props) {
  const intl = useIntl()
  const [selected, setSelected] = useState<number | null>(savedLevel)
  const [saveLevel, {loading: saving}] = useMutation(UPDATE_AGENT_ONBOARDING_COMMISSION_LEVEL)
  const [sendProposal, {loading: sending}] = useMutation(
    SEND_AGENT_ONBOARDING_COMMISSION_PROPOSAL
  )

  useEffect(() => {
    setSelected(savedLevel)
  }, [savedLevel])

  const percent = (level: number) => `${level}%`

  const handleSave = async () => {
    if (selected === null) return
    try {
      const res = await saveLevel({variables: {applicationId, commissionLevel: selected}})
      const payload = res.data?.updateAgentOnboardingCommissionLevel
      if (!payload?.success) {
        toast.error(payload?.errors?.[0]?.message ?? intl.formatMessage(M.saveFailed))
        return
      }
      toast.success(intl.formatMessage(M.saved))
      onSaved()
    } catch {
      toast.error(intl.formatMessage(M.saveFailed))
    }
  }

  const handleSend = async () => {
    try {
      const res = await sendProposal({variables: {applicationId}})
      const payload = res.data?.sendAgentOnboardingCommissionProposal
      if (!payload?.success) {
        toast.error(payload?.errors?.[0]?.message ?? intl.formatMessage(M.sendFailed))
        return
      }
      toast.success(intl.formatMessage(M.sent))
      onSaved()
    } catch {
      toast.error(intl.formatMessage(M.sendFailed))
    }
  }

  return (
    <Card>
      <Card.Header className='border-0 pt-6'>
        <h3 className='fw-bold text-gray-900'>
          <i className='bi bi-percent me-2 text-primary' />
          {intl.formatMessage(M.title)}
        </h3>
      </Card.Header>
      <Card.Body className='pt-2'>
        <div className='text-gray-500 fs-7 mb-3'>{intl.formatMessage(M.label)}</div>
        <div className='d-flex flex-wrap gap-2 mb-4'>
          {LEVELS.map((level) => (
            <button
              key={level}
              type='button'
              disabled={!canEdit}
              className={`btn btn-sm ${
                selected === level ? 'btn-primary' : 'btn-light border border-gray-300'
              }`}
              onClick={() => setSelected(level)}
            >
              {percent(level)}
            </button>
          ))}
        </div>

        <div className='text-gray-600 fs-7 mb-1'>
          {intl.formatMessage(M.savedLevel, {
            level: savedLevel === null ? intl.formatMessage(M.none) : percent(savedLevel),
          })}
        </div>
        {sentAt && (
          <div className='text-gray-500 fs-8 mb-4'>
            {intl.formatMessage(M.sentAt, {date: new Date(sentAt).toLocaleString()})}
          </div>
        )}

        {canEdit && (
          <div className='d-flex flex-wrap gap-3 mt-4'>
            <button
              type='button'
              className='btn btn-light-primary'
              disabled={saving || selected === null || selected === savedLevel}
              onClick={handleSave}
            >
              <i className='bi bi-save me-1' />
              {intl.formatMessage(M.save)}
            </button>
            <button
              type='button'
              className='btn btn-primary'
              // The email carries the saved level, so an unsaved change must not be sendable.
              disabled={sending || savedLevel === null || selected !== savedLevel}
              onClick={handleSend}
            >
              <i className='bi bi-send me-1' />
              {intl.formatMessage(M.send)}
            </button>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}
